import type { TBeneficiaryAttachmentItem } from "@/modules/beneficiary/common/types";
import { CloudflareAsset } from "@/shared/common/enums";
import { CloudFlareImage } from "@/shared/components";
import { MoreIcon } from "@/shared/components/SvgIcons";
import { ButtonIcon } from "@ab.uitools/ui-library/components/ButtonIcon";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Menu } from "@ab.uitools/ui-library/components/Menu";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  item: TBeneficiaryAttachmentItem;
  index: number;
  onEdit: (item: TBeneficiaryAttachmentItem, index: number) => void;
  onDelete: (item: TBeneficiaryAttachmentItem, index: number) => void;
}

export const AttachedItem = ({ item, index, onEdit, onDelete }: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [actionMenuRef, setActionMenuRef] = useState<HTMLDivElement | null>(null);

  const menuItems = [
    {
      label: t("general.edit"),
      value: "edit_attached",
      handler: () => onEdit(item, index),
      iconProps: {
        Component: (props: ISVGIconProps) => (
          <CloudFlareImage {...props} path={CloudflareAsset.Edit}/>
        ),
      },
    },
    {
      label: t("general.delete"),
      value: "delete_attached",
      handler: () => onDelete(item, index),
      iconProps: {
        Component: (props: ISVGIconProps) => (
          <CloudFlareImage {...props} path={CloudflareAsset.Trash}/>
        ),
      },
    },
  ];

  return (
    <div className="card-account-content-wrapper">
      {!!index && (
        <Divider color="light" type="primary" isHorizontal/>
      )}
      <div className="card-account-body-wrapper flexbox align-items--center full-width">
        <div className="card-account-body flexbox flex-direction--column full-width">
          <div className="flexbox justify-content--between align-items--center pr-16">
            <Text weight="bold" size="medium">{item.number}</Text>
            {item.bankName && (
              <Text weight="bold" type="secondary" size="small">
                {item.bankName}
              </Text>
            )}
          </div>
          {item.note && (
            <Text
              weight="bold"
              type="secondary"
              size="small"
              className="card-account-number"
            >
              {item.note}
            </Text>
          )}
        </div>
        <div ref={setActionMenuRef} className="flexbox align-items--center">
          <ButtonIcon
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(prev => !prev);
            }}
            data-testid="open-attached-item-action-menu"
            data-ga-id="open-attached-item-action-menu"
            data-ga-action="Open attached item action menu"
            size="large"
            className={classNames("item-action-button", { "active": isOpen })}
            iconProps={{
              Component: MoreIcon,
              type: "secondary",
            }}
          />
          <Menu
            position={Positions.BOTTOM_RIGHT}
            onClose={() => setIsOpen(false)}
            isOpen={isOpen}
            parentRef={actionMenuRef}
            additionalRef={actionMenuRef}
            menuItems={menuItems}
          />
        </div>
      </div>
    </div>
  );
};
